import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Printer, Anchor, AlertCircle, CheckCircle2, MapPin, Share2 } from "lucide-react";
import { api, fmtDate, fmtNum, GROUP_COLORS } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import ShareModal from "./ShareModal";

/**
 * Squad-wide "Lab Deviations" report. One PDF per click — shows every athlete
 * with their most-recent-lab deviations, plus a squad summary band on the
 * first page. Same print stylesheet as the per-athlete report so the layout
 * stays consistent across browsers.
 */
export default function SquadIssuesReport({ open, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);
  const restoreTimerRef = useRef(null);
  const prevTitleRef = useRef(null);

  useEscClose(open && !showShare, onClose);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError("");
    api
      .get("/squad/issues")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load squad issues"));
  }, [open]);

  if (!open) return null;

  const doPrint = () => {
    if (restoreTimerRef.current) {
      clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }
    if (prevTitleRef.current === null) prevTitleRef.current = document.title;
    const printedOn = new Date()
      .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      .replace(/,/g, "");
    document.title = `YCH Squad Lab Deviations ${printedOn}`;
    document.body.classList.add("printing-report");

    const restore = () => {
      if (prevTitleRef.current !== null) {
        document.title = prevTitleRef.current;
        prevTitleRef.current = null;
      }
      document.body.classList.remove("printing-report");
      if (restoreTimerRef.current) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    restoreTimerRef.current = setTimeout(restore, 4000);
    setTimeout(() => window.print(), 120);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-[#040D1A]/95 backdrop-blur-sm overflow-y-auto"
      data-testid="squad-issues-modal"
      id="history-report-portal-content"
    >
      {/* Toolbar — hidden in print */}
      <div
        className="sticky top-0 z-10 bg-[#040D1A]/80 backdrop-blur-xl border-b border-[#1E304B]"
        data-noprint-toolbar
      >
        <div className="max-w-[1100px] mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Squad-wide lab deviations report
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={doPrint}
              disabled={!data}
              className="pill active disabled:opacity-50"
              data-testid="squad-issues-print-btn"
            >
              <Printer size={14} /> Print / Save as PDF
            </button>
            <button
              onClick={() => setShowShare(true)}
              disabled={!data}
              className="pill disabled:opacity-50"
              data-testid="squad-issues-share-btn"
              title="Share this squad report via WhatsApp or Email"
            >
              <Share2 size={14} /> Share
            </button>
            <button onClick={onClose} className="pill" data-testid="squad-issues-close-btn">
              <X size={14} /> Close
            </button>
          </div>
        </div>
      </div>

      {/* PRINT AREA — same #print-root convention as the per-athlete report */}
      <div id="print-root" className="max-w-[1100px] mx-auto p-6 md:p-10">
        {error && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-4 py-3 text-rose-200">
            {error}
          </div>
        )}
        {!data && !error ? (
          <div className="text-center text-slate-500 py-20">Compiling squad report…</div>
        ) : (
          data && <SquadReport data={data} />
        )}
      </div>

      <ShareModal
        open={showShare}
        onClose={() => setShowShare(false)}
        subject="YCH Squad — Lab Deviations Report"
        bodyTemplate={
          data
            ? `Hi {dr},\n\nSharing the YCH Squad lab deviations report.\n\nSummary:\n• Athletes scanned: ${data.total_athletes}\n• With deviations: ${data.athletes_with_issues}\n• Total deviations across the squad: ${data.total_deviations}\n\nThe full PDF is attached.\n\nThanks.`
            : ""
        }
        contextLabel="YCH Squad — Lab Deviations"
      />
    </div>,
    document.body,
  );
}

function SquadReport({ data }) {
  const today = new Date().toISOString().slice(0, 10);
  const athletesWithIssues = data.athletes.filter((a) => a.issue_count > 0);
  const athletesClean = data.athletes.filter(
    (a) => a.issue_count === 0 && a.latest_lab_date,
  );
  const athletesNoData = data.athletes.filter((a) => a.latest_lab_date === null);

  return (
    <div className="report-doc">
      {/* HEADER */}
      <header className="border-b-2 border-cyan-400/60 pb-5 mb-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-cyan-500/15 border border-cyan-500/40">
              <Anchor size={22} className="text-cyan-300" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300/80">
                Yacht Club of Hyderabad — Squad Lab Deviations
              </div>
              <div className="font-display font-extrabold text-3xl mt-0.5 text-slate-100">
                Squad-wide Health Snapshot
              </div>
              <div className="text-xs text-slate-400 mt-1">
                Most recent lab result per athlete · normal values omitted
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-400 font-mono-data">
            <div>Report generated</div>
            <div className="text-slate-200 font-display text-base">{fmtDate(today)}</div>
          </div>
        </div>
      </header>

      {/* SQUAD SUMMARY BAND */}
      <section className="grid grid-cols-4 gap-3 mb-6" data-testid="squad-summary-band">
        <SummaryCard label="Athletes scanned" value={data.total_athletes} accent="#00E5FF" />
        <SummaryCard
          label="With deviations"
          value={data.athletes_with_issues}
          accent={data.athletes_with_issues === 0 ? "#10B981" : "#EF4444"}
        />
        <SummaryCard
          label="All clear"
          value={data.athletes_clean}
          accent="#10B981"
        />
        <SummaryCard
          label="Total deviations"
          value={data.total_deviations}
          accent="#F59E0B"
        />
      </section>

      {/* TOP RECURRING DEFICIENCIES — squad-level view */}
      {data.top_recurring && data.top_recurring.length > 0 && (
        <section className="mb-6 page-block">
          <SectionTitle>Top recurring deviations across the squad</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.top_recurring.map((t) => (
              <div
                key={t.key}
                className="border border-rose-500/40 bg-rose-500/5 rounded-lg px-3 py-2"
              >
                <div className="text-[10px] uppercase tracking-widest text-rose-300/70">{t.group}</div>
                <div className="text-slate-100 font-display text-sm leading-tight mt-0.5">
                  {t.label}
                </div>
                <div className="font-mono-data text-rose-300 text-xs mt-1">
                  {t.count}× across athletes
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PER-ATHLETE SECTIONS */}
      {athletesWithIssues.length > 0 && (
        <section className="mb-8" data-testid="athletes-with-issues">
          <SectionTitle>
            <AlertCircle size={14} className="inline mr-1.5 text-rose-300" />
            Athletes with deviations ({athletesWithIssues.length})
          </SectionTitle>
          <div className="space-y-3">
            {athletesWithIssues.map((a) => (
              <AthleteBlock key={a.id} athlete={a} />
            ))}
          </div>
        </section>
      )}

      {/* CLEAN SQUAD LIST — compact, single block */}
      {athletesClean.length > 0 && (
        <section className="mb-6 page-block" data-testid="athletes-clean">
          <SectionTitle>
            <CheckCircle2 size={14} className="inline mr-1.5 text-emerald-300" />
            All values within range ({athletesClean.length})
          </SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {athletesClean.map((a) => (
              <div
                key={a.id}
                className="border border-emerald-500/30 bg-emerald-500/5 rounded px-2.5 py-1.5 text-xs flex items-center justify-between"
              >
                <span className="text-slate-100 truncate">{a.name}</span>
                <span className="font-mono-data text-emerald-300 text-[10px] ml-2 shrink-0">
                  {fmtDate(a.latest_lab_date)}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NO LAB DATA */}
      {athletesNoData.length > 0 && (
        <section className="mb-6 page-block" data-testid="athletes-no-data">
          <SectionTitle>No lab record on file ({athletesNoData.length})</SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
            {athletesNoData.map((a) => (
              <div
                key={a.id}
                className="border border-slate-700/70 rounded px-2.5 py-1.5 text-xs text-slate-400"
              >
                {a.name}
              </div>
            ))}
          </div>
        </section>
      )}

      <footer className="mt-10 pt-4 border-t border-slate-800 text-[10px] text-slate-500 flex justify-between">
        <span>Yacht Club of Hyderabad — confidential squad medical record</span>
        <span className="font-mono-data">Reference ranges per Tata 1mg lab; status flags are advisory.</span>
      </footer>
    </div>
  );
}

function AthleteBlock({ athlete }) {
  return (
    <article
      className="break-inside-avoid border border-slate-700/70 rounded-lg p-3"
      data-testid={`squad-athlete-${athlete.id}`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2 mb-2 pb-2 border-b border-slate-800">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-display text-slate-100 font-semibold text-base">{athlete.name}</span>
          <span
            className="px-2 py-0.5 rounded-full text-[10px]"
            style={{
              background: `${GROUP_COLORS[athlete.group] || "#94A3B8"}22`,
              color: GROUP_COLORS[athlete.group] || "#94A3B8",
              border: `1px solid ${GROUP_COLORS[athlete.group] || "#94A3B8"}66`,
            }}
          >
            {athlete.group}
          </span>
          {athlete.subgroup && (
            <span className="px-2 py-0.5 rounded-full text-[10px] border border-cyan-500/40 text-cyan-200 bg-cyan-500/10 font-mono-data">
              {athlete.subgroup}
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-400 flex items-center gap-2">
          <span className="font-mono-data text-cyan-300">{fmtDate(athlete.latest_lab_date)}</span>
          {athlete.latest_lab_hospital && (
            <span className="flex items-center gap-1 text-slate-500">
              <MapPin size={10} /> {athlete.latest_lab_hospital}
            </span>
          )}
          <span className="font-mono-data text-rose-300">
            {athlete.issue_count} flag{athlete.issue_count === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      <div className="space-y-1.5">
        {athlete.issues.map((iss) => (
          <DeviationRow key={iss.key} iss={iss} />
        ))}
      </div>
    </article>
  );
}

function DeviationRow({ iss }) {
  const isHigh = iss.status === "high";
  const accent = isHigh ? "#EF4444" : "#A78BFA";
  const label = isHigh ? "HIGH" : "LOW";
  const refStr =
    iss.range_low != null && iss.range_high != null
      ? `${fmtNum(iss.range_low, 2)} – ${fmtNum(iss.range_high, 2)}`
      : iss.range_low != null
      ? `≥ ${fmtNum(iss.range_low, 2)}`
      : iss.range_high != null
      ? `≤ ${fmtNum(iss.range_high, 2)}`
      : "—";
  const devStr =
    iss.deviation != null
      ? `${isHigh ? "+" : "−"}${fmtNum(Math.abs(iss.deviation), 2)} ${iss.unit || ""}`.trim()
      : null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 border rounded px-2.5 py-1.5 break-inside-avoid"
      style={{ borderColor: `${accent}55`, background: `${accent}0C` }}
    >
      <div className="min-w-[160px]">
        <div className="text-slate-100 text-sm">{iss.label}</div>
        <div className="text-[9px] text-slate-500 uppercase tracking-wider">{iss.group}</div>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-mono-data font-bold text-base" style={{ color: accent }}>
          {fmtNum(iss.value, 2)}
        </span>
        <span className="text-[10px] text-slate-500">{iss.unit || ""}</span>
      </div>
      <span
        className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data"
        style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}
      >
        {label}
      </span>
      <div className="ml-auto flex items-center gap-3 text-right">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Ref</div>
          <div className="font-mono-data text-[11px] text-slate-300">{refStr}</div>
        </div>
        {devStr && (
          <div className="min-w-[90px]">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">Δ</div>
            <div className="font-mono-data text-[11px]" style={{ color: accent }}>
              {devStr}
              {iss.deviation_pct != null && (
                <span className="text-[9px] text-slate-500 ml-1">
                  ({isHigh ? "+" : "−"}
                  {Math.abs(iss.deviation_pct)}%)
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className="border border-slate-700/70 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div
        className="font-display font-extrabold text-2xl mt-0.5"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-display text-base text-slate-100 mb-2 pb-1 border-b border-slate-800">
      {children}
    </h2>
  );
}
